import { useEffect, useState } from "react";
import { X, ExternalLink, Trash2, Plus } from "lucide-react";
import { ArticleService } from "../../services/articleServic";
import { toast } from "react-toastify";
import AddJournal from "./addJournals";

const ProjectJournalList = ({
  projectId,
  documentId,
  isOpen,
  onClose,
}) => {
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedJournalId, setSelectedJournalId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showAddJournalModal, setShowAddJournalModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!isOpen || !projectId || !documentId) return;

    let cancelled = false;

    const fetchJournals = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await ArticleService.getJournals(
          documentId,
          projectId,
        );

        if (!cancelled) {
          setJournals(response?.data || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to fetch journals.");
          setJournals([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchJournals();

    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId, documentId,refreshKey]);

  const confirmDelete = (journalId) => {
    setSelectedJournalId(journalId);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedJournalId) return;

    try {
      setDeletingId(selectedJournalId);

      const response = await ArticleService.deleteProjectJournal(
        selectedJournalId
      );

      if (response?.success === false) {
        throw new Error(
          response?.message || "Failed to delete journal."
        );
      }

      setJournals((prev) =>
        prev.filter((journal) => journal.id !== selectedJournalId)
      );

      toast.success(
        response?.message || "Journal deleted successfully."
      );

      setShowDeleteModal(false);
      setSelectedJournalId(null);
    } catch (error) {
      toast.error(error?.message || "Failed to delete journal.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
  <>
    {isOpen && (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
    
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="journal-modal-title"
        className="w-full max-w-4xl overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between border-b px-3 md:px-6 py-4">
          <div className="order-2 md:order-1">
            <h2
              id="journal-modal-title"
              className="text-lg font-semibold text-gray-800"
            >
              Project Journals
            </h2>

            <p className="mt-1 text-xs text-gray-500">
              Project ID: {projectId}
            </p>
          </div>

          <div className="flex items-center gap-2 order-1 md:order-2 ml-auto">
            <button
              type="button"
              onClick={() => setShowAddJournalModal(true)}
              className="inline-flex items-center gap-2 rounded-md bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800"
            >
              <Plus size={16} />
               Journal
            </button>

            <button
              type="button"
              onClick={onClose}
              aria-label="Close modal"
              className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        <div className="max-h-[70vh] overflow-y-auto p-3 md:p-6">
          {loading ? (
            <p className="py-8 text-center text-sm text-gray-500">
              Loading journals...
            </p>
          ) : error ? (
            <p className="py-8 text-center text-sm text-red-600">
              {error}
            </p>
          ) : journals.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              No journals found for this document.
            </p>
          ) : (
            <div className="overflow-x-auto">
               
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:hidden gap-4">
                {journals.map((journal) => (
                  <div
                    key={journal.id}
                    className={`rounded-lg border shadow-sm overflow-hidden ${
                      journal.isArticleAdded
                        ? "border-green-300"
                        : "border-gray-200"
                    }`}
                  >
                    {/* Card Header */}
                    <div
                      className={`flex items-center justify-between gap-2 px-4 py-3 border-b ${
                        journal.isArticleAdded
                          ? "bg-green-100"
                          : "bg-gray-50"
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 mb-1">
                          Journal Code
                        </p>
                        <p className="font-semibold text-gray-800">
                          {journal.journalCode || "N/A"}
                        </p>
                      </div>

                      {journal.isArticleAdded && (
                        <span className="shrink-0 inline-flex items-center rounded-full bg-green-200 px-2.5 py-1 text-xs font-medium text-green-800">
                          Article Added
                        </span>
                      )}
                    </div>

                    {/* Card Body */}
                    <div className="p-4 space-y-4">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                          Journal Name
                        </p>
                        <p
                          className={`text-sm font-semibold break-words ${
                            journal.isArticleAdded
                              ? "text-green-800"
                              : "text-gray-800"
                          }`}
                        >
                          {journal.journalName || "N/A"}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                            Quartile
                          </p>
                          <span className="inline-flex rounded-md bg-purple-100 px-2.5 py-1 text-xs font-semibold text-purple-800">
                            {journal.quartile || "N/A"}
                          </span>
                        </div>

                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                            Journal Link
                          </p>

                          {journal.link ? (
                            <a
                              href={journal.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline"
                            >
                              View Journal
                              <ExternalLink size={14} />
                            </a>
                          ) : (
                            <span className="text-sm text-gray-500">N/A</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div className="flex justify-end border-t border-gray-200 px-4 py-2">
                      <button
                        type="button"
                        onClick={() => confirmDelete(journal.id)}
                        aria-label={`Delete ${journal.journalName || "journal"}`}
                        title="Delete journal"
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full min-w-[600px] text-left text-sm">
                  <thead className="bg-gray-100 text-xs uppercase text-gray-600 border">
                    <tr>
                      <th className="px-4 py-2">Code</th>
                      <th className="px-4 py-2">Journal Name</th>
                      <th className="px-4 py-2">Quartile</th>
                      <th className="px-4 py-2">Link</th>
                      <th className="px-4 py-2">Action</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {journals.map((journal) => (
                      <tr key={journal.id} className="hover:bg-gray-50">
                        <td className="px-4 py-1 font-medium border-l border-b">
                          {journal.journalCode || "N/A"}
                        </td>

                        <td
                          className={`px-4 py-1 border-b ${
                            journal.isArticleAdded
                              ? "bg-green-100 text-green-800"
                              : ""
                          }`}
                        >
                          {journal.journalName || "N/A"}
                        </td>

                        <td className="px-4 py-1 border-b">
                          {journal.quartile || "N/A"}
                        </td>

                        <td className="px-4 py-1 border-b">
                          {journal.link ? (
                            <a
                              href={journal.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                            >
                              View <ExternalLink size={14} />
                            </a>
                          ) : (
                            "N/A"
                          )}
                        </td>

                        <td className="px-4 py-1 border-r border-b">
                          <button
                            type="button"
                            onClick={() => confirmDelete(journal.id)}
                            aria-label={`Delete ${journal.journalName || "journal"}`}
                            title="Delete journal"
                            className="inline-flex items-center gap-1 rounded-md p-2 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>
          )}
        </div>

        {/* Modal footer */}
        <div className="flex justify-end border-t px-3 md:px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>

      {showDeleteModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center
            bg-black/50 p-4"
          onClick={() => {
            if (!deletingId) {
              setShowDeleteModal(false);
              setSelectedJournalId(null);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-journal-title"
            className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center
                justify-center rounded-full bg-red-100 text-red-600">
                <Trash2 size={22} />
              </div>

              <div>
                <h3
                  id="delete-journal-title"
                  className="text-lg font-semibold text-gray-800"
                >
                  Delete Journal?
                </h3>

                <p className="mt-2 text-sm text-gray-600">
                  Are you sure you want to delete this journal?
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                disabled={Boolean(deletingId)}
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedJournalId(null);
                }}
                className="rounded-md border border-gray-300 px-4 py-2
                  text-sm text-gray-700 hover:bg-gray-50
                  disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleDelete}
                disabled={Boolean(deletingId)}
                className="rounded-md bg-red-600 px-4 py-2 text-sm
                  font-medium text-white hover:bg-red-700
                  disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deletingId ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    )}

    <AddJournal
        isOpen={showAddJournalModal}
        onCancel={() => setShowAddJournalModal(false)}
        documentId={documentId}
        projectId={projectId}
        onSuccess={() => {
          setShowAddJournalModal(false);
          setRefreshKey((prev) => prev + 1);
        }}
    />
  </>
  );
  
};

export default ProjectJournalList;